import type { BillingMode, ModelPrice, SpendAccount } from '@orgus/protocol';
import { Pencil, Wallet, WalletCards } from '@orgus/ui/icons';
import { useState } from 'react';
import { DataTable, EmptyState, ErrorState, IconButton, RelativeTime, Segmented, toUiError } from '@orgus/ui';
import type { Column, UiError } from '@orgus/ui';
import { AccountGlyph, ApiKeyHint, RenameAccountDialog, SOURCE_LABEL, accountName, billingLabel, kindLabel } from '../../costs/accounts.tsx';
import { setBilling, useCosts } from '../../costs/useCosts.ts';
import type { SettingsSection } from '../registry.ts';

const usd = (n: number) => `$${n < 1 ? n.toFixed(n < 0.1 ? 3 : 2) : n.toFixed(2)}`;

const columns: Column<ModelPrice>[] = [
  { key: 'model', header: 'Model', grow: true, text: (p) => `${p.label} ${p.match}`, cell: (p) => (
    <span className="flex flex-col">
      <span className="text-fg">{p.label}</span>
      <span className="font-mono text-caption text-fg-subtle">{p.match}</span>
    </span>
  ) },
  { key: 'input', header: 'Input', type: 'number', cell: (p) => usd(p.input) },
  { key: 'output', header: 'Output', type: 'number', cell: (p) => usd(p.output) },
  { key: 'read', header: 'Cache read', type: 'number', cell: (p) => usd(p.cacheRead) },
  { key: 'w5', header: 'Cache write 5 min', type: 'number', hideBelow: 'md', cell: (p) => usd(p.cacheWrite) },
  { key: 'w1', header: 'Cache write 1 h', type: 'number', hideBelow: 'md', cell: (p) => usd(p.cacheWrite1h) },
];

function accountColumns(onRename: (a: SpendAccount) => void): Column<SpendAccount>[] {
  return [
    {
      key: 'account',
      header: 'Account',
      grow: true,
      text: (a) => `${accountName(a)} ${a.label}`,
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2">
          <AccountGlyph kind={a.kind} />
          <span className="truncate text-fg">{accountName(a)}</span>
          {a.name && <span className="hidden truncate text-caption text-fg-subtle md:inline">{a.label}</span>}
        </span>
      ),
    },
    { key: 'kind', header: 'Provider', hideBelow: 'md', text: (a) => kindLabel(a.kind), cell: (a) => <span className="text-fg-muted">{kindLabel(a.kind)}</span> },
    { key: 'billing', header: 'Billing', text: (a) => billingLabel(a.billing), cell: (a) => <span className="text-fg-muted">{billingLabel(a.billing)}</span> },
    { key: 'source', header: 'Known from', hideBelow: 'lg', text: (a) => SOURCE_LABEL[a.source], cell: (a) => <span className="text-fg-subtle">{SOURCE_LABEL[a.source]}</span> },
    { key: 'seen', header: 'Last seen', type: 'number', hideBelow: 'md', sortValue: (a) => a.lastSeen, cell: (a) => <RelativeTime ts={a.lastSeen} /> },
    {
      key: 'actions',
      header: '',
      cell: (a) => (
        <IconButton size="xs" label={`Rename ${accountName(a)}`} icon={<Pencil />} onClick={() => onRename(a)} />
      ),
    },
  ];
}

function SpendSettings() {
  const costs = useCosts();
  const [renaming, setRenaming] = useState<SpendAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  if (!costs) {
    return <EmptyState icon={<Wallet />} title="This hub does not report spend" description="Restart the hub on the current Orgus version to track spend and budgets." />;
  }
  const change = async (b: BillingMode) => {
    setBusy(true);
    setError(null);
    try {
      await setBilling(b);
    } catch (e) {
      setError(toUiError(e, { what: 'The billing setting was not saved.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className="text-ui font-medium text-fg">How you pay for Claude Code</h3>
        <Segmented<BillingMode>
          label="How you pay for Claude Code"
          size="md"
          options={[
            { value: 'subscription', label: 'Subscription seats (Pro, Max, Team)', disabled: busy },
            { value: 'api', label: 'API key, billed per token', disabled: busy },
          ]}
          value={costs.billing}
          onChange={(b) => void change(b)}
        />
        <p className="max-w-[640px] text-caption text-fg-muted">
          {costs.billing === 'subscription'
            ? 'Spend is shown as notional cost: what the same tokens would cost at API list prices. Your subscription has no per-token bill, so use it to compare work, not to reconcile an invoice.'
            : 'Spend is tokens from Claude Code transcripts at API list prices. Discounts, regional endpoints and batch pricing are not applied, so your invoice can differ.'}
        </p>
        {error && <ErrorState error={error} variant="inline" />}
      </section>
      {costs.accounts && (
        <section className="flex flex-col gap-2">
          <h3 className="text-ui font-medium text-fg">Accounts</h3>
          <p className="max-w-prose text-caption text-fg-muted">
            Found automatically for each session. Subscription accounts count as notional, usage-billed ones at list price; the setting above covers the rest.
          </p>
          <ApiKeyHint costs={costs} />
          <DataTable
            label="Accounts"
            rows={costs.accounts}
            columns={accountColumns(setRenaming)}
            rowKey={(a) => a.key}
            pageSize={false}
            initialSort={{ key: 'seen', dir: 'desc' }}
            empty={<EmptyState icon={<WalletCards />} title="No accounts yet" description="Each session's account appears here once its machine reports it." />}
          />
          <RenameAccountDialog account={renaming} onOpenChange={(open) => !open && setRenaming(null)} />
        </section>
      )}
      <section className="flex flex-col gap-2">
        <h3 className="text-ui font-medium text-fg">Price list</h3>
        <p className="text-caption text-fg-muted">
          US dollars per million tokens, version {costs.pricing.version}. Each usage row keeps the version it was costed with, so a price change never rewrites history.
          Fast mode and long Haiku 5.5 prompts are priced separately.{' '}
          <a href={costs.pricing.source} target="_blank" rel="noreferrer" className="text-fg underline-offset-2 hover:underline">
            Source
          </a>
        </p>
        <DataTable label="Model prices" rows={costs.pricing.prices} columns={columns} rowKey={(p) => p.match} pageSize={false} />
      </section>
    </div>
  );
}

const section: SettingsSection = {
  id: 'spend',
  title: 'Spend',
  description: 'Billing mode, accounts and the price list behind every cost.',
  order: 10,
  group: 'money',
  keywords: ['billing', 'prices', 'cost', 'budget', 'tokens', 'accounts', 'foundry', 'bedrock', 'vertex', 'api key'],
  icon: Wallet,
  Component: SpendSettings,
};
export default section;
